import React from 'react';
import { Link } from 'react-router-dom';

const WhatIsHumanAI = () => {
  return (
    <div className="bg-black min-h-screen text-white pt-20">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">
                What Is HumanAI?
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl text-gray-300 mb-8">
              Design. Test. Deploy. All in One Stack.
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              HumanAI is an end-to-end platform for public opinion research — built for speed, realism, and actionable insights. Whether you're designing surveys, testing political messages, or analyzing open-ended feedback, HumanAI makes it radically easier, faster, and more accurate.
            </p>
          </div>

          {/* Why HumanAI Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Why HumanAI?
            </h2>
            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
              <p>
                Traditional surveys are broken. They’re expensive, slow, and increasingly unreliable. Researchers struggle to recruit participants, trust the data, and move fast enough to stay relevant.
              </p>
              <p>
                HumanAI solves this by combining intelligent survey design tools, rapid message testing, and built-in AI analytics — giving you a powerful end-to-end workflow that saves time and boosts confidence.
              </p>
              <div className="bg-gradient-to-r from-pink/10 to-blue/10 border-l-4 border-pink p-6 rounded-r-lg">
                <p className="text-xl font-semibold text-white mb-2">From question to insight — in minutes, not months.</p>
                <p>
                  With HumanAI, you can design professional-grade surveys with an AI copilot, test your content on realistic agents, and instantly analyze responses across demographics. All in one seamless platform.
                </p>
              </div>
            </div>
          </section>

          {/* Capabilities Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What Can You Do with HumanAI?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              HumanAI is a <strong className="text-white">full-stack research platform</strong> that brings design, testing, and analysis into a unified flow — no panel providers or survey engineers required.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[{
                title: "Design Surveys with AI",
                description: "Write better questions, faster. Our AI assistant helps you frame, reword, and structure surveys for clarity, neutrality, and rigor — personalized to your audience.",
                icon: "📝"
              },
              {
                title: "Test Messages Before You Launch",
                description: "Preview how your audience might respond to ads, narratives, or policy language — before spending a dollar on distribution. Message testing made fast and iterative.",
                icon: "💬"
              },
              {
                title: "Analyze Open-Ended Feedback Automatically",
                description: "Drop in qualitative data — and get instant breakdowns of top themes and emotions by demographic group. Stop guessing what people meant.",
                icon: "📊"
              },
              {
                title: "Run Virtual A/B Tests at Scale",
                description: "Simulate frame experiments and exposure conditions on demand. Compare versions side by side — using agents modeled to reflect your audience.",
                icon: "🧪"
              }].map((capability, index) => (
                <div key={index} className="bg-gray-900/50 border border-gray-800 p-6 rounded-xl hover:border-pink/50 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <span className="text-3xl">{capability.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        ✅ {index + 1}. <span className="text-pink">{capability.title}</span>
                      </h3>
                      <p className="text-gray-300 leading-relaxed">
                        {capability.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Synthetic Agents Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              Simulating Human Behavior with AI
            </h2>
            <p className="text-lg text-gray-300 mb-6">
              At the core of HumanAI is a synthetic population of AI agents — each modeled after real-world demographic distributions and imbued with narrative and behavioral context.
            </p>
            <div className="space-y-6">
              {[{
                title: "Demographic Grounding",
                description: "Agents reflect distributions across gender, race, age, income, education, and more — helping ensure your research mirrors reality.",
                icon: "🔹"
              }, {
                title: "Narrative Intelligence",
                description: "Every agent has a backstory — with jobs, values, relationships, and past experiences — which adds depth and realism to their responses.",
                icon: "🔹"
              }, {
                title: "Behavioral Realism",
                description: "Our models exhibit real human patterns: cognitive biases, emotional shifts, and inconsistent reasoning. Because humans aren't robots — and your research shouldn't pretend they are.",
                icon: "🔹"
              }].map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <span className="text-blue text-2xl mt-1">{feature.icon}</span>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Who Is It For Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              Who Is HumanAI For?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[{
                icon: "🗳️", title: "Political Campaigns", description: "Refine messaging, test frames, and understand voters at scale."
              }, {
                icon: "📊", title: "Market Researchers", description: "Replace panels and focus groups with rapid, reproducible experiments."
              }, {
                icon: "🧠", title: "Think Tanks", description: "Explore public opinion with tools designed for nuance, speed, and rigor."
              }, {
                icon: "🏛️", title: "Advocacy Orgs", description: "Craft persuasive narratives grounded in data, not instinct."
              }, {
                icon: "🧪", title: "Academics", description: "Deploy and analyze behavioral studies — without waiting for IRB or funding."
              }].map((audience, index) => (
                <div key={index} className="bg-gray-900/30 border border-gray-800 p-6 rounded-xl hover:border-blue/50 transition-all duration-300">
                  <div className="text-3xl mb-3">{audience.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-2">{audience.title}</h3>
                  <p className="text-gray-300 text-sm">{audience.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Why It Matters Section */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Why HumanAI Matters
            </h2>
            <div className="bg-gradient-to-r from-pink/10 to-blue/10 border border-pink/30 p-8 rounded-xl">
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                The future of research is fast, flexible, and grounded in realism. With HumanAI, you can skip the slow, costly survey cycles — and make better decisions, faster.
              </p>
              <p className="text-xl font-bold text-center">
                From survey to story, HumanAI delivers.
              </p>
            </div>
          </section>


          {/* Related Resources */}
          <section className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              Related Resources
            </h2>
            
            <div className="space-y-4">
              {[
                { title: "Why HumanAI is Rebuilding the Future of Survey Research", link: "/blog/revolutionizing-survey-research" },
                { title: "How Anthology Makes Virtual Personas Feel Real", link: "/blog/anthology-virtual-personas" },
                { title: "The AI Twin Project: A New Frontier in Human Simulation", link: "/blog/ai-twin-project" }
              ].map((resource, index) => (
                <Link 
                  key={index}
                  to={resource.link}
                  className="block p-4 bg-gray-900/30 border border-gray-800 rounded-lg hover:border-blue/50 hover:bg-gray-900/50 transition-all duration-300"
                >
                  <span className="text-blue hover:text-blue/80 font-medium">
                    - {resource.title}
                  </span>
                </Link>
              ))}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default WhatIsHumanAI;
